import React, { useCallback, useEffect, useRef, useState } from 'react';
import './DockablePanel.css';

const STORAGE_KEY_PREFIX = 'explorer.panelLayout.';

function getStorageKey(panelId, storageKey) {
    return storageKey || `${STORAGE_KEY_PREFIX}${panelId}`;
}

function readLayout(layoutKey) {
    try {
        const saved = window.localStorage.getItem(layoutKey);
        return saved ? JSON.parse(saved) : null;
    } catch (_) {
        return null;
    }
}

function writeLayout(layoutKey, layout) {
    try {
        window.localStorage.setItem(layoutKey, JSON.stringify(layout));
    } catch (_) { }
}

export default function DockablePanel({
    panelId,
    storageKey,
    title,
    defaultCollapsed = false,
    defaultFloating = false,
    defaultWidth,
    defaultHeight,
    defaultPosition,
    initialX = 120,
    initialY = 120,
    onClose,
    children,
}) {
    const savedLayoutRef = useRef(null);
    const initialLayoutRef = useRef(null);
    const layoutKey = getStorageKey(panelId, storageKey);

    if (!initialLayoutRef.current) {
        const saved = readLayout(layoutKey);
        savedLayoutRef.current = saved;
        initialLayoutRef.current = {
            collapsed: saved?.collapsed ?? defaultCollapsed,
            floating: saved?.floating ?? defaultFloating,
            position: saved?.position ?? defaultPosition ?? { x: initialX, y: initialY },
            size: saved?.size ?? {
                width: defaultWidth,
                height: defaultHeight,
            },
        };
    }

    const [collapsed, setCollapsed] = useState(initialLayoutRef.current.collapsed);
    const [floating, setFloating] = useState(initialLayoutRef.current.floating);
    const [position, setPosition] = useState(initialLayoutRef.current.position);
    const [size, setSize] = useState(initialLayoutRef.current.size);
    const dragging = useRef(false);
    const dragOffset = useRef({ dx: 0, dy: 0 });
    const panelRef = useRef(null);

    useEffect(() => {
        writeLayout(layoutKey, { collapsed, floating, position, size });
    }, [collapsed, floating, layoutKey, position, size]);

    useEffect(() => {
        if (!floating || typeof ResizeObserver === 'undefined') return undefined;
        const panel = panelRef.current;
        if (!panel) return undefined;

        const observer = new ResizeObserver(([entry]) => {
            // Use borderBoxSize so measurements match the inline width/height we
            // write back (page sets `* { box-sizing: border-box }` globally;
            // contentRect would shrink by border/padding on every cycle and
            // cause a feedback loop down to min-width/min-height).
            const box = entry.borderBoxSize?.[0];
            const width = box ? box.inlineSize : entry.contentRect.width;
            const height = box ? box.blockSize : entry.contentRect.height;
            setSize(prev => {
                const next = {
                    width: Math.round(width),
                    height: Math.round(height),
                };
                if (prev?.width === next.width && prev?.height === next.height) {
                    return prev;
                }
                return next;
            });
        });

        observer.observe(panel);
        return () => observer.disconnect();
    }, [floating]);

    const toggleCollapsed = useCallback(() => {
        setCollapsed((value) => !value);
    }, []);

    const tearOff = useCallback(() => {
        const saved = savedLayoutRef.current || readLayout(layoutKey);
        const savedPosition = saved?.position;

        if (savedPosition) {
            setPosition(savedPosition);
        } else if (panelRef.current) {
            const rect = panelRef.current.getBoundingClientRect();
            setPosition({
                x: Math.max(0, rect.left + 40),
                y: Math.max(0, rect.top + 40),
            });
        }

        setFloating(true);
    }, [layoutKey]);

    const dockBack = useCallback(() => {
        setFloating(false);
    }, []);

    const onMouseDown = useCallback((event) => {
        if (!floating || event.target.closest('button')) return;

        event.preventDefault();
        dragging.current = true;
        dragOffset.current = {
            dx: event.clientX - position.x,
            dy: event.clientY - position.y,
        };
    }, [floating, position]);

    useEffect(() => {
        const onMouseMove = (event) => {
            if (!dragging.current) return;

            const nextX = Math.max(0, Math.min(event.clientX - dragOffset.current.dx, window.innerWidth - 100));
            const nextY = Math.max(0, Math.min(event.clientY - dragOffset.current.dy, window.innerHeight - 40));
            setPosition({ x: nextX, y: nextY });
        };

        const onMouseUp = () => {
            dragging.current = false;
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, []);

    const className = [
        'dockable-panel',
        floating ? 'dockable-panel--floating' : 'dockable-panel--docked',
        collapsed ? 'dockable-panel--collapsed' : '',
    ].filter(Boolean).join(' ');

    const panelSize = floating
        ? {
            width: size?.width,
            height: collapsed ? undefined : size?.height,
        }
        : undefined;

    return (
        <div
            ref={panelRef}
            className={className}
            style={floating ? { left: position.x, top: position.y, ...panelSize } : panelSize}
        >
            <div className="dockable-panel-header" onMouseDown={onMouseDown}>
                <span className="dockable-panel-title">{title}</span>
                <div className="dockable-panel-controls">
                    <button
                        type="button"
                        className="dockable-panel-btn"
                        onClick={toggleCollapsed}
                        title={collapsed ? 'Expand panel' : 'Collapse panel'}
                        aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}
                    >
                        {collapsed ? '▸' : '▾'}
                    </button>
                    {!floating && (
                        <button
                            type="button"
                            className="dockable-panel-btn"
                            onClick={tearOff}
                            title="Tear off panel"
                            aria-label="Tear off panel"
                        >
                            ⇱
                        </button>
                    )}
                    {floating && (
                        <button
                            type="button"
                            className="dockable-panel-btn"
                            onClick={dockBack}
                            title="Dock panel"
                            aria-label="Dock panel"
                        >
                            ⇲
                        </button>
                    )}
                    {onClose && (
                        <button
                            type="button"
                            className="dockable-panel-btn"
                            onClick={onClose}
                            title="Close panel"
                            aria-label="Close panel"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>
            {!collapsed && (
                <div className="dockable-panel-body">
                    {children}
                </div>
            )}
        </div>
    );
}
